<template>
	<div class="flex flex-col items-start gap-8px">
		<div class="flex flex-row gap-12px">
			<div
				v-for="(_, index) in otp"
				:key="index"
				class="flex flex-col items-center"
			>
				<input
					type="text"
					inputMode="numeric"
					@input="handleInput($event, index)"
					@keydown="handleKeyDown($event, index)"
					@click="onClick(index)"
					@paste="handlePaste"
					:value="otp[index]"
					:class="inputClasses"
					:disabled="disabled"
					maxlength="2"
					ref="otpInput"
				/>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, type Ref } from 'vue'

const props = defineProps({
	length: {
		type: Number,
		default: 6,
	},
	isError: {
		type: Boolean,
		default: false,
	},
	value: {
		type: String,
		default: '',
	},
	disabled: {
		type: Boolean,
		default: false,
	},
})

const emit = defineEmits(['input'])
const otp = ref(Array(props.length).fill(''))
const otpInput: Ref<HTMLInputElement[]> = ref([])
const indexCurrent = ref(0)

watch(
	() => props.value,
	(newVal, oldVal) => {
		const newLength = newVal.length
		const oldLength = oldVal.length
		const maxLength = props.length

		const isForward = newLength >= oldLength
		const isBackward = newLength < oldLength

		if (isForward && newLength <= maxLength) {
			if (newLength === oldLength) {
				moveFocus(indexCurrent.value + 1)
			} else {
				moveFocus(newLength)
			}
		} else if (isBackward) {
			if (otp.value[indexCurrent.value] === '') {
				moveFocus(indexCurrent.value)
			}
		}

		otp.value = newVal
			.split('')
			.concat(Array(props.length).fill(''))
			.slice(0, props.length)
	},
)

const inputClasses = computed(() => [
	{
		'otp-inputs__input': true,
		error: props.isError && !props.disabled,
		disabled: props.disabled,
	},
])

const handleInput = (e: Event, index: number) => {
	if (props.disabled) return

	const inputTarget = e.target as HTMLInputElement

	if (/\D/g.test(inputTarget.value)) {
		// no idea why bind value break
		inputTarget.value = otp.value[index]
		return
	}

	indexCurrent.value = index
	const value = inputTarget.value.replace(/\D/g, '').slice(-1)

	otp.value[index] = value
	// no idea why bind value break
	inputTarget.value = value

	emit('input', otp.value.join(''))

	if (props.value === otp.value.join('')) {
		moveFocus(index + 1)
	}
}

const handleKeyDown = (e: KeyboardEvent, index: number) => {
	if (props.disabled) return
	if (
		!/^[0-9]{1}$/.test(e.key) &&
		e.key !== 'Backspace' &&
		e.key !== 'Delete' &&
		e.key !== 'Tab' &&
		!e.metaKey
	) {
		e.preventDefault()
	}
	if (e.key === 'ArrowLeft' || e.keyCode === 37) {
		moveFocus(index - 1)
	}
	if (e.key === 'ArrowRight' || e.keyCode === 39) {
		moveFocus(index + 1)
	}
	if (e.key === 'Delete' || e.key === 'Backspace') {
		if (index > 0) {
			if (otp.value[index] === '') {
				moveFocus(index - 1)
			}
		}
	}
}

const handlePaste = (e: ClipboardEvent) => {
	if (props.disabled) return
	e.preventDefault()
	const text = e.clipboardData?.getData('text') || ''
	if (!new RegExp(`^[0-9]{${otp.value.length}}$`).test(text)) {
		return
	}
	const digits = text.split('')
	digits.forEach((digit, index) => {
		otp.value[index] = digit
	})
	moveFocus(otp.value.length - 1)
	emit('input', otp.value.join(''))
}

function moveFocus(index: number) {
	const otpInputRef = otpInput.value[index]
	if (otpInputRef) {
		otpInputRef.focus()
	}
}

function onClick(index: number) {
	otpInput.value[index].setSelectionRange(1, 1)
}

onMounted(() => {
	otpInput.value = otpInput.value.slice(0, props.length)
})
</script>
